import React, { useEffect, useRef } from 'react';
import { Eye, Target, Heart } from 'lucide-react';
import { useTheme } from './Navbar';

const useScrollAnimation = (threshold = 0.1) => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.querySelectorAll('.fade-up, .fade-left, .fade-right, .scale-in').forEach((child) => {
            child.classList.add('visible');
          });
          observer.unobserve(el);
        }
      },
      { threshold }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold]);
  return ref;
};

const pillars = [
  {
    icon: Eye,
    title: 'Visión',
    description:
      'Mantenerse en un alto rango de competitividad dentro del giro que nos ocupa, adoptando un compromiso de respeto con el medio ambiente y de impulso en el desarrollo y mejor calidad de vida de cada uno de los integrantes de esta empresa.',
  },
  {
    icon: Target,
    title: 'Objetivo General',
    description:
      'Cumplir con los trabajos contratados con la calidad, costo y tiempo a satisfacción del cliente e involucrados clave. Desarrollando relaciones a largo plazo con clientes, proveedores e integrantes del equipo.',
  },
  {
    icon: Heart,
    title: 'Misión',
    description:
      'Proveer al estado de servicios de alta calidad en la construcción y conservación de infraestructura municipal, estatal y federal cooperando con el desarrollo y mejor calidad de vida de nuestros pueblos.',
  },
];

const values = [
  { label: 'Responsabilidad', desc: 'Asumimos cada compromiso con seriedad y profesionalismo.' },
  { label: 'Respeto', desc: 'Valoramos a nuestros colaboradores, clientes y comunidades.' },
  { label: 'Comunicación', desc: 'Mantenemos informado a nuestro cliente en todo momento.' },
  { label: 'Compromiso', desc: 'Entregamos lo prometido con calidad y en tiempo.' },
];

const Philosophy = () => {
  const ref = useScrollAnimation(0.08);
  const { isDark } = useTheme();

  return (
    <section
      style={{
        padding: '8rem 2rem',
        background: isDark ? '#1a1a1a' : '#F7F7F7',
        position: 'relative',
        overflow: 'hidden',
      }}
      ref={ref}
    >
      {/* Top accent */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: 3,
        background: 'var(--red)',
      }} />

      <div style={{
        maxWidth: 1200,
        margin: '0 auto',
      }}>
        {/* Header */}
        <div className="fade-up" style={{ textAlign: 'center', marginBottom: '5rem' }}>
          <div className="section-label">Nuestra filosofía</div>
          <h2 style={{
            fontFamily: 'var(--font-heading)',
            fontWeight: 900,
            fontSize: 'clamp(2rem, 4vw, 3rem)',
            color: isDark ? '#ffffff' : '#111111',
            marginTop: '0.5rem',
          }}>
            Los pilares que nos definen
          </h2>
        </div>

        {/* Vision / Objetivo / Mision */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: '2rem',
          marginBottom: '5rem',
        }} className="pillars-grid">
          {pillars.map((pillar, i) => (
            <div
              key={i}
              className={`fade-up delay-${i + 1}`}
              style={{
                background: isDark ? '#242424' : '#ffffff',
                borderRadius: 16,
                padding: '2.5rem 2rem',
                boxShadow: isDark ? '0 4px 24px rgba(0,0,0,0.3)' : '0 4px 24px rgba(0,0,0,0.06)',
                border: isDark ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.04)',
                position: 'relative',
                overflow: 'hidden',
                transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                cursor: 'default',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-6px)';
                e.currentTarget.style.boxShadow = isDark ? '0 20px 60px rgba(0,0,0,0.5)' : '0 20px 60px rgba(0,0,0,0.12)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = isDark ? '0 4px 24px rgba(0,0,0,0.3)' : '0 4px 24px rgba(0,0,0,0.06)';
              }}
            >
              {/* Top bar */}
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: 4,
                background: 'var(--red)',
              }} />

              <div style={{
                width: 52,
                height: 52,
                background: 'rgba(196,30,58,0.12)',
                borderRadius: 12,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '1.5rem',
              }}>
                <pillar.icon size={24} color="var(--red)" strokeWidth={1.5} />
              </div>

              <h3 style={{
                fontFamily: 'var(--font-heading)',
                fontWeight: 800,
                fontSize: '1.1rem',
                color: 'var(--red)',
                marginBottom: '1rem',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
              }}>{pillar.title}</h3>

              <p style={{
                fontFamily: 'var(--font-body)',
                fontSize: '0.9rem',
                color: isDark ? 'rgba(255,255,255,0.7)' : '#374151',
                lineHeight: 1.8,
                textAlign: 'justify',
              }}>{pillar.description}</p>
            </div>
          ))}
        </div>

        {/* Values */}
        <div className="fade-up">
          <div style={{ textAlign: 'center', marginBottom: '2.5rem' }}>
            <h3 style={{
              fontFamily: 'var(--font-heading)',
              fontWeight: 800,
              fontSize: '1.5rem',
              color: isDark ? '#ffffff' : '#111111',
            }}>Nuestros Valores</h3>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: '1.5rem',
          }} className="values-grid">
            {values.map((v, i) => (
              <div
                key={i}
                className={`scale-in delay-${i + 1}`}
                style={{
                  textAlign: 'center',
                  padding: '2rem 1.5rem',
                  background: isDark ? '#242424' : '#ffffff',
                  borderRadius: 12,
                  boxShadow: isDark ? '0 2px 16px rgba(0,0,0,0.3)' : '0 2px 16px rgba(0,0,0,0.05)',
                  border: isDark ? '1px solid rgba(255,255,255,0.08)' : '1px solid transparent',
                  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                  cursor: 'default',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.boxShadow = isDark ? '0 12px 40px rgba(0,0,0,0.5)' : '0 12px 40px rgba(0,0,0,0.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = isDark ? '0 2px 16px rgba(0,0,0,0.3)' : '0 2px 16px rgba(0,0,0,0.05)';
                }}
              >
                <div style={{
                  width: 10,
                  height: 10,
                  background: 'var(--red)',
                  borderRadius: '50%',
                  margin: '0 auto 1rem auto',
                }} />
                <h4 style={{
                  fontFamily: 'var(--font-heading)',
                  fontWeight: 700,
                  fontSize: '1rem',
                  color: isDark ? '#ffffff' : '#111111',
                  marginBottom: '0.5rem',
                }}>{v.label}</h4>
                <p style={{
                  fontFamily: 'var(--font-body)',
                  fontSize: '0.82rem',
                  color: isDark ? 'rgba(255,255,255,0.6)' : '#374151',
                  lineHeight: 1.7,
                }}>{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <style>{`
        @media (max-width: 900px) {
          .pillars-grid { grid-template-columns: 1fr !important; }
          .values-grid { grid-template-columns: repeat(2, 1fr) !important; }
        }
        @media (max-width: 480px) {
          .values-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </section>
  );
};

export default Philosophy;
